import { useGenericCart } from '@contexts/GenericCartContext/GenericCartContext';
import { showToast } from '@design-system';
import {
    GetUsersResponse,
    useGetUsersMutation,
    useGetEpiOrdersMutation,
    useUpdateEpiOrderMutation,
    EpiOrder,
    EpiOrderStatuses
} from '@services';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const useEpiOrderCartPageVM = () => {
    const { id } = useParams();
    const { items, addItem, removeItem, updateQuantity, clearCart, setSavedOrderId, savedOrderId, saveCart } = useGenericCart({
        type: 'epi'
    });
    const navigate = useNavigate();

    const [order, setOrder] = useState<EpiOrder | undefined>(undefined);
    const [operators, setOperators] = useState<GetUsersResponse['operators']>([]);
    const [operatorsOptions, setOperatorsOptions] = useState<{ id: string; label: string }[]>([]);
    const [worksitesOptions, setWorksitesOptions] = useState<{ id: string; label: string }[]>([]);
    const [selectedOperatorId, setSelectedOperatorId] = useState<string>('');
    const [selectedWorksiteId, setSelectedWorksiteId] = useState<string>('');
    const [deliveryDate, setDeliveryDate] = useState<string>('');
    const [comment, setComment] = useState<string>('');

    const [showModal, setShowModal] = useState<boolean>(false);
    const [cartName, setCartName] = useState<string>('');

    const isFirstMount = useRef(true);

    const [getEpiOrders, { isLoading: isGettingOrders, data: orders }] = useGetEpiOrdersMutation();
    const [updateEpiOrder, { isLoading: isUpdatingOrder, data: updatedOrder }] = useUpdateEpiOrderMutation();
    const [getUsers, { data: users }] = useGetUsersMutation();

    const actions = [
        {
            label: 'Enregistrer le panier dans les favoris',
            onClick: () => setShowModal(true)
        }
    ];

    // TODO : refactor this, since localStorage access is dirty...
    // But it only works like that, not with useState nor useRef
    useEffect(() => {
        return () => {
            if (!isFirstMount.current && localStorage.getItem('isAddingProduct') !== 'true') {
                clearCart();
                setSavedOrderId(null);
            }
            isFirstMount.current = false;
            localStorage.removeItem('isAddingProduct');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleUpdateOrder = async () => {
        if (!id || !order || isUpdatingOrder) return;
        updateEpiOrder({
            id,
            operator_id: Number(selectedOperatorId),
            id_worksite: Number(selectedWorksiteId),
            date_order: deliveryDate || new Date().toISOString(),
            comment,
            products: items.map((item) => ({ id: Number(item.id), quantity: Number(item.quantity) }))
        });
    };

    const handleOperatorChange = (operatorId: string) => {
        setSelectedOperatorId(operatorId);
        setSelectedWorksiteId('');

        const selectedOperator = operators.find((op) => op.id_operator === operatorId);
        if (selectedOperator) {
            const worksites = selectedOperator.worksites.map((worksite) => ({
                id: worksite.id,
                label: worksite.name
            }));
            setWorksitesOptions(worksites);
        } else {
            setWorksitesOptions([]);
        }
    };

    const handleSaveCart = async () => {
        const response = await saveCart(cartName, items);
        if (response === 'success') {
            setShowModal(false);
            setCartName('');
            showToast({
                message: 'Panier enregistré avec succès',
                type: 'success'
            });
        } else {
            showToast({
                message: "Une erreur est survenue lors de l'enregistrement du panier",
                type: 'error'
            });
        }
    };

    const handleAddProduct = () => {
        localStorage.setItem('isAddingProduct', 'true');
        navigate('/sante-securite/epi');
    };

    useEffect(() => {
        if (id) {
            getEpiOrders();
            getUsers(undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (orders && id) {
            setOrder(orders.find((order) => order.id === id));
        }
    }, [orders, id]);

    useEffect(() => {
        if (users) {
            setOperators(users.operators);
        }
    }, [users]);

    useEffect(() => {
        if (operators) {
            setOperatorsOptions(
                operators.map((operator) => ({
                    id: operator.id_operator,
                    label: operator.name
                }))
            );
        }
    }, [operators]);

    useEffect(() => {
        if (order && users && operators) {
            const isNewOrder = !savedOrderId || savedOrderId !== Number(order.id);

            if (isNewOrder) {
                clearCart();
                setSavedOrderId(Number(order.id));

                order.products.forEach((product) => {
                    addItem({
                        id: product.id,
                        label: product.label,
                        quantity: Number(product.quantity),
                        packing_unit: product.packing_unit,
                        image: product.image
                    });
                });
            }

            handleOperatorChange(`${order.id_operator}`);
            setSelectedWorksiteId(`${order.id_worksite}`);
            setComment(order.comment);
            setDeliveryDate(order.date_order.split(' ')[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order, users, operators]);

    useEffect(() => {
        if (updatedOrder) {
            showToast({
                message: 'Commande mise à jour avec succès',
                type: 'success'
            });
            navigate(`/sante-securite/epi/orders`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUpdatingOrder, updatedOrder]);

    return {
        actions,
        order,
        items,
        isLoading: isGettingOrders || isUpdatingOrder,
        removeItem,
        updateQuantity,
        editable: order?.status === EpiOrderStatuses.CREATED,
        handleUpdateOrder,
        selectedWorksiteId,
        setSelectedWorksiteId,
        comment,
        setComment,
        deliveryDate,
        setDeliveryDate,
        selectedOperatorId,
        worksitesOptions,
        operatorsOptions,
        handleOperatorChange,
        handleAddProduct,
        showModal,
        setShowModal,
        cartName,
        setCartName,
        handleSaveCart
    };
};
